import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { StageBadge, TipoClienteIcon, LoadingState, EmptyState } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Link2, Gift } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RECOMPENSA_STYLES = {
  conseguida: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pendiente: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  perdida: 'bg-zinc-700/30 text-zinc-500 border-zinc-600/30',
};

const RECOMPENSA_LABELS = {
  conseguida: 'Conseguida',
  pendiente: 'Pendiente',
  perdida: 'Perdida',
};

export default function ReferidosPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referidos, setReferidos] = useState([]);

  const fetchReferidos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/referidos`, { withCredentials: true });
      setReferidos(response.data.referidos || []);
    } catch (err) {
      console.error('Error fetching referidos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReferidos(); }, [fetchReferidos]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const conseguidas = referidos.filter((r) => r.recompensa === 'conseguida').length;

  return (
    <ComercialLayout
      title="Referidos"
      subtitle={`${referidos.length} referidos · ${conseguidas} recompensas conseguidas`}
      onLogout={handleLogout}
    >
      <div className="space-y-4" data-testid="referidos-page">
        {loading ? (
          <LoadingState />
        ) : referidos.length === 0 ? (
          <EmptyState
            icon={Link2}
            title="Sin referidos"
            description="Los contactos con origen 'Referido' aparecerán aquí con su estado y recompensa."
          />
        ) : (
          <div className="space-y-3">
            {referidos.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/comercial/cliente/${r.id}`)}
                className="w-full p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/20 text-left transition-colors"
                data-testid={`referido-card-${r.client_code}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <TipoClienteIcon tipo={r.tipo_cliente} />
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">{r.cliente}</p>
                      <p className="text-xs text-zinc-500">
                        <span className="font-mono">{r.client_code}</span>
                        {r.referido_por && <span> · Referido por: <span className="text-zinc-400">{r.referido_por}</span></span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StageBadge stage={r.estado} label={r.estado_label} />
                    <span className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${RECOMPENSA_STYLES[r.recompensa]}`}>
                      <Gift className="w-3 h-3" />
                      {RECOMPENSA_LABELS[r.recompensa]}
                    </span>
                  </div>
                </div>
                {/* Evolución - barra de progreso */}
                <div className="mt-3">
                  <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${r.progreso || 0}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">Evolución: {r.progreso || 0}%</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </ComercialLayout>
  );
}
